import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getCompanyById } from '../api/company';
import { createUser } from '../api/user';
import { APP_ROUTES } from '../routes';
import Loader from '../components/Loader';

const CreateCompanyAdmin: React.FC = () => {
    const { id: companyId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const { data: company, isLoading, isError } = useQuery({ 
        queryKey: ['company', companyId], 
        queryFn: () => getCompanyById(companyId!), 
        enabled: !!companyId 
    });

    const createUserMutation = useMutation({
        mutationFn: createUser,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] }); // Invalidate users query
            toast.success('Admin for ' + company?.name + ' created successfully!');
            navigate(APP_ROUTES.COMPANY);
        },
        onError: (error: Error) => {
            toast.error(`Failed to create admin: ${error.message}`);
        }
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!companyId) return;
        createUserMutation.mutate({ name, email, password, company_id: companyId, roles: ['admin'] });
    };

    if (isLoading) return <Loader />;
    if (isError || !company) return <div>Error loading company details.</div>;

    return (
        <div className="p-6 max-w-lg mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-md">
                <h1 className="text-2xl font-bold mb-2">Create Administrator</h1>
                <p className="mb-6 text-gray-600">You are creating an admin for the company: <span className="font-bold">{company.name}</span></p>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="admin-name" className="block text-sm font-medium text-gray-700">Name *</label>
                        <input type="text" id="admin-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" required />
                    </div>
                    <div>
                        <label htmlFor="admin-email" className="block text-sm font-medium text-gray-700">Email *</label>
                        <input type="email" id="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" required />
                    </div>
                    <div>
                        <label htmlFor="admin-password" className="block text-sm font-medium text-gray-700">Password *</label>
                        <input type="password" id="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" required />
                    </div>
                    <div className="flex justify-end space-x-2 pt-4">
                        <button type="button" onClick={() => navigate(APP_ROUTES.COMPANY)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md">Cancel</button>
                        <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md" disabled={createUserMutation.isPending}>
                            {createUserMutation.isPending ? 'Creating Admin...' : 'Create Admin'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateCompanyAdmin;
